"use client";

import { Faro } from "@grafana/faro-web-sdk";
import { clientLogger } from "../logger";

interface UserContext {
	id: string;
	email?: string;
	guestUserId?: string;
	sessionId?: string;
}

class FaroManager {
	private static instance: FaroManager;
	private isInitialized = false;
	private initPromise: Promise<void> | null = null;
	private queuedLogs: Array<() => void> = [];
	private currentUser: UserContext | null = null;
	private faro: Faro | null = null;

	private constructor() {
		// Private constructor to enforce singleton
	}

	public static getInstance(): FaroManager {
		if (!FaroManager.instance) {
			FaroManager.instance = new FaroManager();
		}
		return FaroManager.instance;
	}

	public setFaro(faroInstance: Faro): void {
		this.faro = faroInstance;
		this.markInitialized();
	}

	public markInitialized(): void {
		this.isInitialized = true;
		// Process any queued logs
		while (this.queuedLogs.length > 0) {
			const log = this.queuedLogs.shift();
			try {
				log?.();
			} catch (error) {
				// Silently handle errors from queued logs
				console.debug("[Faro] Error processing queued log:", error);
			}
		}
	}

	public waitForInitialization(): Promise<void> {
		if (this.isInitialized) {
			return Promise.resolve();
		}

		if (!this.initPromise) {
			this.initPromise = new Promise((resolve) => {
				// Check every 100ms for 10 seconds
				let attempts = 0;
				const maxAttempts = 100;
				const interval = setInterval(() => {
					if (this.isInitialized || (this.faro && this.faro.api)) {
						clearInterval(interval);
						this.isInitialized = true;
						resolve();
					} else if (attempts++ >= maxAttempts) {
						clearInterval(interval);
						clientLogger.warn(
							"[Faro] Initialization timed out after 10 seconds",
						);
						resolve(); // Resolve anyway to not block the application
					}
				}, 100);
			});
		}

		return this.initPromise;
	}

	public isReady(): boolean {
		return this.isInitialized && !!this.faro?.api;
	}

	public queueLog(logFn: () => void): void {
		if (this.isReady()) {
			logFn();
		} else {
			this.queuedLogs.push(logFn);
		}
	}

	public pushEvent(
		eventName: string,
		attributes?: Record<string, string | number | boolean | null | undefined>,
	): void {
		const sanitizedAttributes =
			attributes &&
			Object.entries(attributes).reduce<Record<string, string>>(
				(acc, [key, value]) => {
					if (value === undefined || value === null) {
						return acc;
					}

					acc[key] = String(value);
					return acc;
				},
				{},
			);

		if (this.isReady() && this.faro) {
			this.faro.api.pushEvent(eventName, sanitizedAttributes);
		} else {
			this.queuedLogs.push(() => {
				if (this.faro) {
					this.faro.api.pushEvent(eventName, sanitizedAttributes);
				}
			});
		}
	}

	/**
	 * Set user context for Faro sessions
	 * This will be attached to all subsequent logs and events
	 */
	public setUserContext(user: UserContext | null): void {
		this.currentUser = user;

		if (!this.faro?.api || !this.isReady()) {
			clientLogger.debug("[Faro] Cannot set user context - Faro not ready");
			return;
		}

		try {
			if (user) {
				// Build attributes object, filtering out undefined/null values
				const attributes: Record<string, string> = {};

				if (user.email) attributes.email = String(user.email);
				if (user.guestUserId) attributes.guestUserId = String(user.guestUserId);

				// Set user context in Faro
				this.faro.api.setUser({
					id: String(user.id),
					email: user.email ? String(user.email) : undefined,
					attributes,
				});

				// Use provided sessionId or fallback to user identifier
				// Ensure sessionId is always a string
				const sessionId = String(
					user.sessionId || user.email || user.guestUserId || user.id,
				);

				this.faro.api.setSession({
					id: sessionId,
					attributes: {
						userId: String(user.id),
						userEmail: user.email ? String(user.email) : "",
						guestUserId: user.guestUserId ? String(user.guestUserId) : "",
					},
				});

				clientLogger.debug("[Faro] User context and session set", {
					userId: user.id,
					email: user.email,
					guestUserId: user.guestUserId,
					sessionId,
				});
			} else {
				// Clear user context and session
				this.faro.api.setUser(undefined);
				this.faro.api.setSession(undefined);
				clientLogger.debug("[Faro] User context and session cleared");
			}
		} catch (error) {
			clientLogger.error("[Faro] Failed to set user context", error as Error);
		}
	}

	/**
	 * Get current user context
	 */
	public getUserContext(): UserContext | null {
		return this.currentUser;
	}

	/**
	 * Add user context to any attributes object
	 * Useful for ensuring user context is included in custom logs
	 */
	public enrichAttributes(
		attributes: Record<string, string | number | boolean> = {},
	): Record<string, string | number | boolean> {
		if (!this.currentUser) {
			return attributes;
		}

		return {
			...attributes,
			"user.id": String(this.currentUser.id),
			"user.email": this.currentUser.email || "",
			"user.guestUserId": this.currentUser.guestUserId || "",
			"session.id": this.currentUser.sessionId || "",
		};
	}

	/**
	 * Create a logger with user context automatically included
	 * This ensures all logs from this logger include user information
	 */
	public createUserLogger(
		context: Record<string, string | number | boolean> = {},
	) {
		const enrichedContext = this.enrichAttributes(context);
		return {
			trace: (
				message: string,
				attributes: Record<string, string | number | boolean> = {},
			) => {
				clientLogger.trace(message, { ...enrichedContext, ...attributes });
			},
			debug: (
				message: string,
				attributes: Record<string, string | number | boolean> = {},
			) => {
				clientLogger.debug(message, { ...enrichedContext, ...attributes });
			},
			info: (
				message: string,
				attributes: Record<string, string | number | boolean> = {},
			) => {
				clientLogger.info(message, { ...enrichedContext, ...attributes });
			},
			warn: (
				message: string,
				attributes: Record<string, string | number | boolean> = {},
			) => {
				clientLogger.warn(message, { ...enrichedContext, ...attributes });
			},
			error: (
				message: string,
				error?: Error,
				attributes: Record<string, string | number | boolean> = {},
			) => {
				clientLogger.error(message, error, {
					...enrichedContext,
					...attributes,
				});
			},
			child: (childContext: Record<string, string | number | boolean>) => {
				return this.createUserLogger({ ...enrichedContext, ...childContext });
			},
		};
	}
}

/**
 * Updates user context from appStore after authentication changes
 * This function should be called whenever auth state changes (login, logout, etc.)
 */
export async function syncUserContextFromAppStore(): Promise<void> {
	if (typeof window === "undefined") return;

	// Get the appStore state directly
	try {
		const appStore = await import("@/store/appStore");
		const { email, deviceId, authorization, user } =
			appStore.useAppStore.getState().data;
		const hasAuthorization = Boolean(authorization);
		const resolvedEmail = (user as User)?.employment?.email || email;

		// Get the Faro manager instance
		const manager = FaroManager.getInstance();
		if (!manager.isReady()) {
			console.debug("[Faro] Not ready yet, skipping user context update");
			return;
		}

		// Generate session ID from store values
		let sessionId: string;
		if (hasAuthorization && resolvedEmail && deviceId) {
			// Logged in user: combine email and deviceId
			sessionId = `${resolvedEmail}_${deviceId}`;
		} else if (deviceId) {
			// Guest user: use deviceId only
			sessionId = deviceId;
		} else {
			// Fallback: generate a temporary session ID
			sessionId = `temp_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
		}

		console.debug("[Faro] Updating user context from appStore", {
			hasAuthorization,
			email: resolvedEmail,
			deviceId,
			sessionId,
		});

		// Set the appropriate user context based on login state
		if (hasAuthorization && resolvedEmail) {
			// User is logged in - use email as user ID, combined session ID
			manager.setUserContext({
				id: resolvedEmail,
				email: resolvedEmail,
				sessionId: sessionId,
			});
		} else if (deviceId) {
			// User is not logged in - use deviceId as both user ID and session ID
			manager.setUserContext({
				id: deviceId,
				guestUserId: deviceId,
				sessionId: sessionId,
			});
		} else {
			// No user context available - use temporary session ID
			manager.setUserContext({
				id: sessionId,
				sessionId: sessionId,
			});
		}
	} catch (error) {
		console.error("[Faro] Error syncing with app store:", error);
	}
}

// Keep the original function for backward compatibility, but make it use the appStore version
export function syncUserContextFromCookies(): void {
	syncUserContextFromAppStore();
}

export const faroManager = FaroManager.getInstance();
